import { Fragment, useEffect, useMemo } from 'react';

import { useSelector } from 'react-redux';

import { Grid, GridColumn } from '@atlaskit/page';

import { Repository } from 'src/components/types';
import { WORKSPACE_REPOSITORIES_LIST_LOAD, MetricSender } from 'src/metrics';
import { useParams } from 'src/router/hooks';
import { RepositoryFilters } from 'src/sections/profile/types';
import { useWorkspaceNavActionUrls } from 'src/sections/workspace/components/navigation/use-workspace-nav-action-urls';
import WorkspaceHelmet from 'src/sections/workspace/components/workspace-helmet';
import { getUpToSmallBreakpointState } from 'src/selectors/global-selectors';
import { CenteredRow } from 'src/styles';
import urls from 'src/urls/dashboard';

import { useWorkspaceRepositories } from '../provider';
import {
  getWorkspaceRepositoriesApi,
  WorkspaceRepositoriesApi,
} from '../requests';
import { WorkspaceRepositoryResource } from '../types';

import Filters from './filters';
import ProfileRepositoriesHeader from './profile-repositories-header';
import messages from './profile-repositories-header.i18n';
import {
  ProfileRepositoriesTable,
  SortField,
} from './profile-repositories-table';
import ProfileUpgradeBanner from './profile-upgrade-banner';

type Props = {
  repositories: Repository[];
  setRepositories: (
    api: WorkspaceRepositoriesApi,
    repositories: Repository[]
  ) => void;
  filters: RepositoryFilters;
  onFilterChange: (filters: RepositoryFilters) => void;
  getPaginationUrl: (selectedPage: number) => string;
  isAscending: boolean;
  page: number | null;
  handleSortChange: (field: SortField) => void;
  sortField: SortField;
  isLoading: boolean;
  isError: boolean;
  sentryId?: string | undefined;
  resourceData: WorkspaceRepositoryResource | null | undefined;
};

export const ProfileRepositoriesPage = (props: Props) => {
  const {
    repositories,
    setRepositories,
    page,
    isAscending,
    sortField,
    filters,
    getPaginationUrl,
    onFilterChange,
    handleSortChange,
    isLoading,
    isError,
    sentryId,
    resourceData,
  } = props;

  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();

  const isUpToSmallBreakpointActive = useSelector(getUpToSmallBreakpointState);
  const { createRepoUrl, importRepoUrl } = useWorkspaceNavActionUrls();
  const isFilterApplied = useMemo(
    () => Object.values(filters).some(v => !!v),
    [filters]
  );

  const [{ buildStatusesFetched }] = useWorkspaceRepositories();

  const api = useMemo(
    () =>
      getWorkspaceRepositoriesApi(urls.api.internal.repositoryBuildStatuses()),
    []
  );

  useEffect(() => {
    if (resourceData) {
      setRepositories(api, resourceData.repositories);
    }
  }, [resourceData, setRepositories, api]);

  return (
    <Fragment>
      <WorkspaceHelmet pageTitle={messages.pageTitle} />
      <Grid
        layout="fluid"
        spacing={isUpToSmallBreakpointActive ? 'cosy' : 'comfortable'}
      >
        <GridColumn>
          <div id="profile-repositories">
            <ProfileRepositoriesHeader />
            <Filters filters={filters} onFilterChange={onFilterChange} />
            <ProfileRepositoriesTable
              page={page}
              getPaginationUrl={getPaginationUrl}
              handleSortChange={handleSortChange}
              isAscending={isAscending}
              isFilterApplied={isFilterApplied}
              repositories={repositories}
              pagelen={resourceData?.pagelen}
              size={resourceData?.size}
              isError={isError}
              sentryId={sentryId}
              isLoading={isLoading}
              workspaceSlug={workspaceSlug}
              createUrl={createRepoUrl}
              importUrl={importRepoUrl}
              buildStatusesFetched={buildStatusesFetched}
              sortField={sortField}
            />
          </div>
          <CenteredRow>
            {!isLoading && !resourceData?.size && !isFilterApplied && (
              <ProfileUpgradeBanner />
            )}
          </CenteredRow>
        </GridColumn>
      </Grid>
      {!isLoading && <MetricSender metric={WORKSPACE_REPOSITORIES_LIST_LOAD} />}
    </Fragment>
  );
};
